import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import {
  useCustomers,
  useCustomerTiers,
  useAssignCustomerTier,
  useAssignPaymentStyle,
  useSyncCustomerWithStripe,
} from "@/api/customers";
import { useSendPasswordResetEmail } from "@/api/use-user-api";
import { useToast } from "@/hooks/use-toast";
import { CreateCustomerButton } from "./create-customer-button";
import { Input } from "@/components/ui/input";

const statusColors: Record<string, string> = {
  ACTIVE: "bg-green-500",
  INACTIVE: "bg-gray-500",
  DELETED: "bg-red-500",
};

const typeColors: Record<string, string> = {
  INDIVIDUAL: "bg-blue-500",
  BUSINESS: "bg-purple-500",
};

const paymentStyleLabels: Record<string, string> = {
  MANUAL: "Manual",
  PAY_PER_ORDER: "Pay Per Order",
  MONTHLY_FIRST: "Monthly (1st)",
  WEEKLY_SUNDAY: "Weekly (Sunday)",
};

export function CustomersTable() {
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [paymentStyleDialogOpen, setPaymentStyleDialogOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null
  );
  const [selectedTier, setSelectedTier] = useState<string>("");
  const [selectedPaymentStyle, setSelectedPaymentStyle] =
    useState<string>("MANUAL");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const {
    data: customersResponse,
    isLoading,
    isFetching,
  } = useCustomers({ page, limit, search: debouncedSearchTerm });

  const { data: tiersResponse } = useCustomerTiers();
  const assignTierMutation = useAssignCustomerTier();
  const assignPaymentStyleMutation = useAssignPaymentStyle();
  const syncWithStripeMutation = useSyncCustomerWithStripe();
  const { toast } = useToast();

  const sendPasswordResetEmail = useSendPasswordResetEmail({
    isCustomer: true,
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Password reset email sent successfully",
      });
    },
  });

  const handleAssignTier = async (customerId: string, tierId: string) => {
    if (!customerId) return;
    try {
      await assignTierMutation.mutateAsync({ customerId, tierId });
      toast({ title: "Success", description: "Customer tier assigned." });
      setTierDialogOpen(false);
      setSelectedTier("");
      setSelectedCustomerId(null);
    } catch (error) {
      console.error("Error assigning tier:", error);
      toast({ title: "Error", description: "Failed to assign tier." });
    }
  };

  const handleAssignPaymentStyle = async (
    customerId: string,
    paymentStyle: string
  ) => {
    if (!customerId) return;
    try {
      await assignPaymentStyleMutation.mutateAsync({
        customerId,
        paymentStyle,
      });
      toast({ title: "Success", description: "Payment style assigned." });
      setPaymentStyleDialogOpen(false);
      setSelectedPaymentStyle("MANUAL");
      setSelectedCustomerId(null);
    } catch (error) {
      console.error("Error assigning payment style:", error);
      toast({ title: "Error", description: "Failed to assign payment style." });
    }
  };

  const handleSyncWithStripe = async (customerId: string) => {
    if (!customerId) return;
    try {
      await syncWithStripeMutation.mutateAsync({ customerId });
      toast({ title: "Success", description: "Sync with Stripe initiated." });
    } catch (error) {
      console.error("Error syncing with Stripe:", error);
      toast({ title: "Error", description: "Failed to sync with Stripe." });
    }
  };

  const handleAssignBusinessAccount = (customerId: string) => {
    console.log("Assign business account to:", customerId);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const customers = customersResponse?.data ?? [];
  const pagination = customersResponse?.pagination;
  const tiers = tiersResponse?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between mb-4 gap-4">
        <CreateCustomerButton />
        <Input
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Business Details</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Payment Style</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center">
                  No customers found matching your search.
                </TableCell>
              </TableRow>
            )}
            {customers.map((customer) => (
              <TableRow key={customer.id}>
                <TableCell className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={customer.user.picture} />
                    <AvatarFallback>
                      {customer.user.firstName?.[0]}
                      {customer.user.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">
                      {customer.user.firstName} {customer.user.lastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      #{customer.customerNumber}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    className={`${typeColors[customer.type]} text-white`}
                    variant="secondary"
                  >
                    {customer.type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    className={`${statusColors[customer.status]} text-white`}
                    variant="secondary"
                  >
                    {customer.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <p>{customer.user.phone}</p>
                  <p className="text-sm text-gray-500">{customer.user.email}</p>
                </TableCell>
                <TableCell>
                  {customer.businessAccount ? (
                    <div>
                      <p className="font-medium">
                        {customer.businessAccount.companyName}
                      </p>
                      <p className="text-sm text-gray-500">
                        Tax #: {customer.businessAccount.taxNumber ?? "N/A"}
                      </p>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">
                      No business account
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {customer.tier ? (
                    <Badge variant="outline">{customer.tier.name}</Badge>
                  ) : (
                    <span className="text-sm text-gray-500">
                      No tier assigned
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {customer.billing?.paymentStyle ? (
                    <Badge variant="outline">
                      {paymentStyleLabels[
                        customer.billing
                          .paymentStyle as keyof typeof paymentStyleLabels
                      ] ?? customer.billing.paymentStyle}
                    </Badge>
                  ) : (
                    <span className="text-sm text-gray-500">
                      Not configured
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          sendPasswordResetEmail.mutate(customer.userId);
                        }}
                        disabled={sendPasswordResetEmail.isPending}
                      >
                        {sendPasswordResetEmail.isPending
                          ? "Sending..."
                          : "Send Password Reset Email"}
                      </DropdownMenuItem>

                      <Dialog
                        open={
                          tierDialogOpen && selectedCustomerId === customer.id
                        }
                        onOpenChange={(open) => {
                          setTierDialogOpen(open);
                          if (!open) setSelectedCustomerId(null);
                        }}
                      >
                        <DialogTrigger asChild>
                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault();
                              setSelectedCustomerId(customer.id);
                              setSelectedTier(customer.tier?.id ?? "");
                              setTierDialogOpen(true);
                            }}
                          >
                            Assign Tier
                          </DropdownMenuItem>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Assign Tier</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <Select
                              value={selectedTier}
                              onValueChange={setSelectedTier}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select a tier">
                                  {selectedTier
                                    ? tiers.find((t) => t.id === selectedTier)
                                        ?.name
                                    : "No tier assigned"}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {tiers.map((tier) => (
                                  <SelectItem key={tier.id} value={tier.id}>
                                    {tier.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              className="w-full"
                              onClick={() =>
                                handleAssignTier(
                                  selectedCustomerId!,
                                  selectedTier
                                )
                              }
                              disabled={
                                assignTierMutation.isPending || !selectedTier
                              }
                            >
                              {assignTierMutation.isPending
                                ? "Assigning..."
                                : "Confirm"}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <Dialog
                        open={
                          paymentStyleDialogOpen &&
                          selectedCustomerId === customer.id
                        }
                        onOpenChange={(open) => {
                          setPaymentStyleDialogOpen(open);
                          if (!open) setSelectedCustomerId(null);
                        }}
                      >
                        <DialogTrigger asChild>
                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault();
                              setSelectedCustomerId(customer.id);
                              setSelectedPaymentStyle(
                                customer.billing?.paymentStyle ?? "MANUAL"
                              );
                              setPaymentStyleDialogOpen(true);
                            }}
                            disabled={!customer.billing}
                          >
                            Assign Payment Style
                          </DropdownMenuItem>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Assign Payment Style</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <Select
                              value={selectedPaymentStyle}
                              onValueChange={setSelectedPaymentStyle}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select a payment style">
                                  {selectedPaymentStyle
                                    ? paymentStyleLabels[selectedPaymentStyle]
                                    : "Select payment style"}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="MANUAL">Manual</SelectItem>
                                <SelectItem value="PAY_PER_ORDER">
                                  Pay Per Order
                                </SelectItem>
                                <SelectItem value="MONTHLY_FIRST">
                                  Monthly (1st)
                                </SelectItem>
                                <SelectItem value="WEEKLY_SUNDAY">
                                  Weekly (Sunday)
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              className="w-full"
                              onClick={() =>
                                handleAssignPaymentStyle(
                                  selectedCustomerId!,
                                  selectedPaymentStyle
                                )
                              }
                              disabled={
                                assignPaymentStyleMutation.isPending ||
                                !selectedPaymentStyle
                              }
                            >
                              {assignPaymentStyleMutation.isPending
                                ? "Assigning..."
                                : "Confirm"}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>

                      {customer.billing && (
                        <DropdownMenuItem
                          onClick={() => handleSyncWithStripe(customer.id)}
                          disabled={syncWithStripeMutation.isPending}
                        >
                          {syncWithStripeMutation.isPending &&
                          syncWithStripeMutation.variables?.customerId ===
                            customer.id
                            ? "Syncing..."
                            : "Sync with Stripe"}
                        </DropdownMenuItem>
                      )}

                      {customer.type === "INDIVIDUAL" && (
                        <DropdownMenuItem
                          onClick={() =>
                            handleAssignBusinessAccount(customer.id)
                          }
                        >
                          Add Business Account
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-gray-500">
          Showing page {pagination?.page ?? 1} of {pagination?.totalPages ?? 1}.
          Total {pagination?.totalCount ?? 0} customers.
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={!pagination?.hasPreviousPage || isFetching}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={!pagination?.hasNextPage || isFetching}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
